import { ShieldCheck, EyeOff, Cpu, ScanFace } from "lucide-react";
import { Reveal } from "../lib/anim";

const FEATURES = [
  {
    icon: Cpu,
    title: "Edge-computing privacy scrubbing",
    desc: "All sensitive processing happens locally on the device — nothing raw ever leaves your hardware.",
  },
  {
    icon: ScanFace,
    title: "Faces & plates auto-blurred",
    desc: "Specialized hardware automatically blurs human faces and vehicle license plates locally before anything uploads.",
  },
  {
    icon: ShieldCheck,
    title: "Audited & antivirus-safe",
    desc: "Completely audited and verified antivirus-safe. Your device and your privacy stay protected.",
  },
];

export const Privacy = () => {
  return (
    <section
      data-testid="privacy-section"
      className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12"
    >
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-16 items-center">
        {/* Visual */}
        <Reveal>
          <div className="relative aspect-square max-w-md rounded-3xl overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1584310881889-93bc9c4577ff"
              alt="Privacy scrubbing at the edge"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0A192F]/60 backdrop-blur-md" />
            <div className="absolute inset-0 gx-grid-bg opacity-40" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8">
              <div className="relative mb-6">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-[#4CAF50]/40 bg-[#4CAF50]/10">
                  <EyeOff size={34} className="text-[#4CAF50]" />
                </div>
              </div>
              <div className="font-mono-gx text-xs uppercase tracking-[0.2em] text-[#4CAF50]">
                Scrubbed at the edge
              </div>
              <p className="mt-2 text-sm text-slate-300 max-w-xs">
                Human faces & license plates are blurred locally — before upload.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Copy */}
        <div>
          <Reveal>
            <p className="font-mono-gx text-xs uppercase tracking-[0.25em] text-[#00B4D8] mb-4">
              / Privacy, Security &amp; Safety
            </p>
            <h2 className="font-display font-black text-3xl md:text-5xl tracking-tight leading-[1.1] max-w-xl">
              Built to earn.{" "}
              <span className="gx-text-gradient">Engineered to protect.</span>
            </h2>
            <p className="mt-5 text-slate-400 max-w-xl leading-relaxed">
              GeoNetix only ever processes automated, unidentifiable road-asset
              imagery. Privacy isn&apos;t a setting — it&apos;s baked into the hardware.
            </p>
          </Reveal>

          <div className="mt-10 space-y-4">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.title} delay={i * 0.08}>
                  <div className="flex gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-5 transition-colors duration-300 hover:border-[#00B4D8]/40">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30">
                      <Icon size={20} className="text-[#00B4D8]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">
                        {f.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
